import { FieldRenderProps } from '@progress/kendo-react-form';
import React, { useContext, useState } from 'react';
import { Upload, UploadListItemProps, UploadOnAddEvent, UploadOnRemoveEvent } from '@progress/kendo-react-upload';
import { GearFieldWrapper } from '../GearFieldWrapper';
import { ExtendedUploadFileInfo } from '../../../../Model/ExtendedUploadFileInfo';
import { SvgIcon } from '@progress/kendo-react-common';
import { downloadIcon, trashIcon } from '@progress/kendo-svg-icons';
import { DocumentsContext } from '../../../../context/documents/DocumentsContext';
import { useGetFileExtensions } from '../../../../context/configuration/useGetFileExtensions';
import { MAX_FILE_SIZE } from '../../../../config/consts';
import { useGlobalizationContext } from '../../../../context/globalization/GlobalizationContext';
import { useTranslation } from '../../../../context/globalization/useTranslation';
import { EditedDocument } from '../../../grid/toolbar/FilterActions.types';

interface FormUploadProps extends FieldRenderProps {
  editedDocument: 'new' | EditedDocument;
  ctName?: string;
  multiple?: boolean;
  filesRenderedElsewhere?: boolean;
  maxFiles?: number;
  onFileSelect?: (files: FileList) => void;
  isProcessing?: boolean;
}

const isFileExtensionAllowed = (fileName: string, allowedExtensions: string[] | undefined): boolean => {
  if (!allowedExtensions || !allowedExtensions.length) {
    return true;
  }

  const fileExtension = fileName.split('.').pop()?.toLowerCase();
  if (!fileExtension) {
    return false;
  }

  return allowedExtensions.includes(fileExtension);
};

export const FormUpload: React.FC<FormUploadProps> = fieldRenderProps => {
  const { disabled, editedDocument, value, multiple, filesRenderedElsewhere, maxFiles, onFileSelect, isProcessing } =
    fieldRenderProps;
  const documentsContext = useContext(DocumentsContext);
  const { data: allowedExtensions } = useGetFileExtensions();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const [validationError, setValidationError] = useState<string | null>(null);

  const currFiles = React.useMemo(() => {
    if (!value) {
      return [];
    }
    if (!Array.isArray(value)) {
      return [value];
    }

    if (multiple) {
      return value.map(item => item.file || item);
    }

    return value;
  }, [value, multiple]);

  const onChangeHandler = (event: UploadOnAddEvent): void => {
    const newFiles = event.newState;

    for (const file of newFiles) {
      if (file && file.name && !isFileExtensionAllowed(file.name, allowedExtensions)) {
        setValidationError(translate('custom.common.validation.fileTypeNotAllowed'));
        return;
      }
    }

    if (maxFiles != null && newFiles.length > maxFiles) {
      if (currFiles.length === maxFiles) {
        setValidationError(translate('custom.common.validation.fileLimitReached'));
      } else {
        setValidationError(translate('custom.common.validation.fileLimitExceeded', { maxFiles }));
      }
      return;
    }

    setValidationError(null);

    if (multiple) {
      const existingFilesWithMetadata = value || [];
      const existingFileNames = new Set(existingFilesWithMetadata.map((item: any) => item.file?.name));

      const newFilesWithMetadata = newFiles
        .filter(file => !existingFileNames.has(file.name))
        .map(file => ({
          file,
          perFileMetadata: {}
        }));

      const mergedFiles = [...existingFilesWithMetadata, ...newFilesWithMetadata];
      fieldRenderProps.onChange({ value: mergedFiles });
    } else {
      fieldRenderProps.onChange({ value: newFiles });
    }

    if (onFileSelect && newFiles.length > 0) {
      const rawFiles = newFiles.map(f => f.getRawFile?.()).filter(Boolean) as File[];

      if (rawFiles.length > 0) {
        const dataTransfer = new DataTransfer();
        rawFiles.forEach(file => dataTransfer.items.add(file));
        onFileSelect(dataTransfer.files);
      }
    }
  };

  const onRemoveHandler = (event: UploadOnRemoveEvent): void => {
    if (multiple) {
      const filesWithMetadata = event.newState.map(file => ({
        file,
        perFileMetadata: {}
      }));
      fieldRenderProps.onChange({ value: filesWithMetadata });
    } else {
      fieldRenderProps.onChange({ value: event.newState });
    }
    setValidationError(null);
  };

  const removeFile = (fileToRemove: ExtendedUploadFileInfo): void => {
    if (multiple) {
      const updatedFiles = currFiles.filter(item => {
        const file = item.file || item;
        return file !== fileToRemove;
      });
      fieldRenderProps.onChange({ value: updatedFiles });
    } else {
      const updatedFiles = currFiles.filter(file => file !== fileToRemove);
      fieldRenderProps.onChange({ value: updatedFiles });
    }
    setValidationError(null);
  };

  const handleDownload = async (documentId: string) => {
    if (editedDocument === 'new' || !documentsContext.openDocument) {
      return;
    }

    try {
      documentsContext.openDocument(editedDocument.Cps_ContentType, documentId);
    } catch {
      // empty
    }
  };

  const customItemRenderer = (props: UploadListItemProps): React.JSX.Element => {
    const extendedFiles = props.files as ExtendedUploadFileInfo[];

    return (
      <>
        {extendedFiles.map((file, index) => (
          <div key={file.uid || index} className="k-file">
            <div className="attachmentItem k-file-single">
              <div
                className="attachmentName"
                onClick={() => file.documentId && handleDownload(file.documentId)}
                style={{ cursor: file.documentId ? 'pointer' : 'default' }}
              >
                {file.name}
              </div>
              <div className="attachmentActionButtons">
                {file.documentId && (
                  <button
                    type="button"
                    className="k-button k-button-md k-button-flat k-button-flat-primary"
                    onClick={() => file.documentId && handleDownload(file.documentId)}
                    title="Download file"
                  >
                    <SvgIcon icon={downloadIcon} size="medium" />
                  </button>
                )}
                <button
                  type="button"
                  className="k-button k-button-md k-button-flat k-button-flat-primary"
                  onClick={() => removeFile(file)}
                  title="Delete file"
                >
                  <SvgIcon icon={trashIcon} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </>
    );
  };

  return (
    <GearFieldWrapper {...fieldRenderProps}>
      <Upload
        autoUpload={false}
        showActionButtons={false}
        showFileList={!filesRenderedElsewhere}
        listItemUI={customItemRenderer}
        files={currFiles}
        onAdd={onChangeHandler}
        onRemove={onRemoveHandler}
        disabled={disabled || isProcessing}
        className="gearFormUpload"
        multiple={multiple}
        restrictions={{
          maxFileSize: MAX_FILE_SIZE,
          allowedExtensions
        }}
      />
      {validationError && (
        <div className="k-form-error" role="alert">
          {validationError}
        </div>
      )}
    </GearFieldWrapper>
  );
};
